import AnimatedPopup from "../core/AnimatedPopup";
import Button from "../core/Button";
import Strings from "../core/data/Strings";
import ResizableScene from "../core/ResizableScene";
import ScaleManager from "../core/ScaleManager";
import GameColors from "../GameColors";
import ARIA from "../utils/ARIA";
import Fonts from "../utils/Fonts";
import LayoutUtils from "../utils/LayoutUtils";

export class PausePopup extends AnimatedPopup {
	onExit: () => void;
	onExitContext: CallbackContext;

	onHelp: () => void;
	onHelpContext: CallbackContext;

	onQuit: () => void;
	onQuitContext: CallbackContext;

	_onResize: () => void;
	private musicKey: string;

	private soundButton: Button;
	private soundButtonText: Phaser.GameObjects.Text;
	private isSoundOn: boolean;

	buttons: Button[];
	constructor(
		scene: ResizableScene,
		onExit: () => void,
		onExitContext: CallbackContext,
		onHelp: () => void,
		onHelpContext: CallbackContext,
		onQuit: () => void,
		onQuitContext: CallbackContext,
		fade = true
	) {
		super(scene, 0, 0);
		this.onExit = onExit;
		this.onExitContext = onExitContext;

		this.setDepth(5);
		this.onHelp = onHelp;
		this.onHelpContext = onHelpContext;

		this.onQuit = onQuit;
		this.onQuitContext = onQuitContext;

		this.buttons = [];

		this.isSoundOn = !scene.soundManager.muted;
		const overlay = (this._overlay = scene.add.sprite(0, 0, "popupOverlay"));
		overlay.setScale(Math.max(scene.viewport.width / overlay.width, scene.viewport.height / overlay.height));

		overlay.setInteractive();
		this.add(overlay);
		const popupContainer = (this._popupGroup = scene.add.container(0, 0));
		this.add(popupContainer);

		const background = scene.add.image(0, 0, "popupSmallBacker");
		//background.setScale(60, 35);
		popupContainer.add(background);
		popupContainer.setSize(background.width, background.height);

		const headerBG = (this._headerBG = scene.add.image(0, 0, "popupHeader"));
		const header = scene.add.text(0, 0, Strings.get("PauseMenu"), {
			color: GameColors.WHITE,
			fontFamily: Fonts.MontserratExtraBold,
			fontSize: "68px",
		});
		header.scaleX = 0.85;
		popupContainer.add([headerBG, header]);

		const buttonContainer = scene.add.container(0, 0);
		popupContainer.add(buttonContainer);
		const buttonBacker = scene.add.image(0, 0, "popupTextBacker");
		buttonContainer.setSize(buttonBacker.width, buttonBacker.height);
		buttonContainer.add(buttonBacker);

		const helpButton = scene.add.button(0, 0, "helpBtn", null, this.onHelpPressed, this, true);

		const continueButton = scene.add.button(0, 0, "nextBtn", null, this.onContinuePressed, this, true);
		continueButton.setScale(0.85);
		popupContainer.add(continueButton);

		const closeButton = scene.add.button(0, 0, "closeBtn", null, this.onContinuePressed, this, true);
		popupContainer.add(closeButton);

		const quitButton = scene.add.button(0, 0, "homeBtn", null, this.onQuitPressed, this, true);

		const soundButton = (this.soundButton = scene.add.button(
			0,
			0,
			this.isSoundOn ? "soundBtnOn" : "soundBtnOff",
			null,
			this.onSoundPressed,
			this,
			true
		));
		buttonContainer.add([helpButton, quitButton, soundButton]);

		this.buttons.push(helpButton, continueButton, quitButton, soundButton);

		const offset = (buttonBacker.width - helpButton.displayWidth * 3) / 4;
		const size = new Phaser.Math.Vector2(helpButton.displayWidth + offset, helpButton.height);
		LayoutUtils.alignGrid(this.scene, [quitButton, helpButton, soundButton], buttonBacker, size, 3);

		for (const button of this.buttons) {
			button.setScale(0.95);
		}
		this._onResize = (): void => {
			ScaleManager.ScaleBackgroundFromZone(overlay, this.scene.viewport);
			scene.alignIn.Center(overlay, scene.viewport);
			scene.alignIn.Center(popupContainer, scene.viewport);
			scene.alignIn.Center(buttonContainer, background, 0, -80);

			scene.alignIn.TopRight(closeButton, background, -5, 10);

			scene.alignIn.TopCenter(headerBG, background, 0, headerBG.height / 2);
			scene.alignIn.Center(header, headerBG, 0, 0);

			scene.alignIn.BottomCenter(continueButton, background, 0, 30);
		};
		this.resize();

		const ariaLayer = "pause-popup";
		header.setARIA(ariaLayer, true);
		closeButton.setARIA("Close Popup", ariaLayer);
		quitButton.setARIA("Home", ariaLayer);
		helpButton.setARIA("Help", ariaLayer);
		soundButton.setARIA("Toggle Sound", ariaLayer);
		continueButton.setARIA("Continue", ariaLayer);
		ARIA.showLayer(ariaLayer, true);
		
		this.tweenIn(fade);
	}

	resize(): void {
		super.resize();
		if (this._onResize != null) this._onResize();
	}

	protected onHelpPressed(): void {
		//this.tweenOut();
		// if (this.onHelp && this.onHelpContext) this.onHelp.call(this.onHelpContext);
		this.tweenOut(false, this.onHelp, this.onHelpContext);
	}

	protected onContinuePressed(): void {
		console.log("continuePressed()");
		// if (this.onExit && this.onExitContext) this.onExit.call(this.onExitContext);
		this.tweenOut(true, this.onExit, this.onExitContext);
	}
	protected onQuitPressed(): void {
		//this.tweenOut();
		// if (this.onQuit && this.onQuitContext) this.onQuit.call(this.onQuitContext);
		this.tweenOut(false, this.onQuit, this.onQuitContext);
	}
	protected onSoundPressed(): void {
		//TODO: Mute sounds
		this.scene.soundManager.muted = !this.scene.soundManager.muted;
		this.scene.sound.mute = this.scene.soundManager.muted;
		this.scene.music.muted = !this.scene.music.muted;
		this.isSoundOn = !this.scene.soundManager.muted;
		if (!this.isSoundOn) this.soundButton.setTexture("soundBtnOff");
		else this.soundButton.setTexture("soundBtnOn");
	}

	tweenOut(fade = true, callback?: () => void, context?: CallbackContext): void {
		const tween = this.scene.add.tween({
			targets: this._popupGroup,
			y: { from: this._popupGroup.y, to: this._popupGroup.y + 1000 },
			duration: 400,
			ease: Phaser.Math.Easing.Back.In,
		});
		if (fade) {
			this.scene.add.tween({
				targets: this._overlay,
				alpha: 0,
				duration: 300,
			});
		}
		if (callback && context) {
			tween.once(Phaser.Tweens.Events.TWEEN_COMPLETE, callback, context);
		}
	}

	tweenIn(fade = true): void {
		this.scene.add.tween({
			targets: this._popupGroup,
			y: { from: this._popupGroup.y + 1000, to: this._popupGroup.y },
			duration: 400,
			ease: Phaser.Math.Easing.Back.Out,
		});
		this._popupGroup.y += 1000;

		if (fade) {
			this.scene.add.tween({
				targets: this._overlay,
				alpha: 0.5,
				duration: 300,
			});
			this._overlay.alpha = 0;
		} else {
			this._overlay.alpha = 0.5;
		}
	}
}
